import React, { memo } from 'react';
import { TabsProps, TabProps, styled, Tabs, Tab } from '@mui/material';
import { useHeaderTabs, TabsConfig, TabItem } from '../../hooks/useHeaderTabs';
import MyBetsCountBadge from '../atoms/MyBetsCountBadge';
import SwipeableLogo from '../icons/SwipeableLogo';

const DEFAULT_CONFIG: TabsConfig = {
  mobile: {
    common: [],
    auth: [
      { label: 'mobileLive', value: '/in-play' },
      { label: '', value: '/sports', icon: <SwipeableLogo size='small' /> },
      { label: 'bets', value: '/my-bets', icon: <MyBetsCountBadge /> },
    ],
    public: [
      { label: 'sports', value: '/sports' },
      { label: 'inPlay', value: '/in-play' },
      { label: 'casino', value: '/casino' },
    ],
  },
  desktop: {
    common: [
      { label: 'sports', value: '/sports' },
      { label: 'inPlay', value: '/in-play' },
      { label: 'casino', value: '/casino' },
    ],
    auth: [
      {
        label: 'myBets',
        value: '/my-bets',
        icon: <MyBetsCountBadge />,
      },
    ],
    public: [],
  },
};

const StyledTabs = styled(({ indicatorWidth, ...props }: TabsProps & { indicatorWidth?: number }) => (
  <Tabs
    {...props}
    slotProps={{
      indicator: {
        children: <span className='MuiTabs-indicatorSpan' />,
      },
    }}
  />
))(({ theme, indicatorWidth }) => ({
  position: 'absolute',
  left: '50%',
  transform: 'translate(-50%, 0)',
  overflow: 'visible',
  '& .MuiTabs-scroller': {
    overflow: 'visible !important',
  },
  '& .MuiTabs-indicator': {
    background: theme.palette.primary.main,
    display: 'flex',
    justifyContent: 'center',
    height: 2,
    bottom: 2,
    [theme.breakpoints.up('md')]: {
      bottom: 6,
    },
  },
  '& .MuiTabs-indicatorSpan': {
    maxWidth: indicatorWidth ?? '100%',
    width: '100%',
    backgroundColor: theme.palette.secondary.main,
  },
  '& .MuiTab-root': {
    overflow: 'visible',
    minWidth: 12,
    [theme.breakpoints.down('md')]: {
      padding: 8,
      ...theme.typography.body3,
    },
    opacity: 1,
    '&:not(.Mui-selected):hover': {
      color: theme.palette.info.main,
    },
  },
}));

const StyledTab = styled((props: TabProps) => <Tab disableRipple {...props} />)(({ theme }) => ({
  ...theme.typography.body2,
  minHeight: 48,
  textTransform: 'none',
  whiteSpace: 'nowrap',
}));

type HeaderTabNavigationProps = {
  config?: TabsConfig;
  TabsComponent?: React.ComponentType<TabsProps & { indicatorWidth?: number }>;
  TabComponent?: React.ComponentType<TabProps>;
  indicatorWidth?: number;
};

const HeaderTabNavigation = memo(
  ({
    config = DEFAULT_CONFIG,
    TabsComponent = StyledTabs,
    TabComponent = StyledTab,
    indicatorWidth = 46,
  }: HeaderTabNavigationProps) => {
    const { showSearchBar, selectedTab, localizedTabs, handleChange } = useHeaderTabs(config);

    if (showSearchBar) return null;

    return (
      <TabsComponent value={selectedTab} onChange={handleChange} indicatorWidth={indicatorWidth} textColor='inherit'>
        {localizedTabs.map((tab: TabItem, index: number) => (
          <TabComponent
            key={tab.label}
            label={tab.label}
            value={index}
            icon={tab.icon}
            onClick={(e) => handleChange(e, index)}
          />
        ))}
      </TabsComponent>
    );
  }
);

export default HeaderTabNavigation;
